import { Button, IconButton, Stack, Typography } from '@mui/material';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import {
  CheckIcon,
  CrossIcon,
  Loading,
  ModalHeaderActions,
  ModalLarge,
  Spin,
  SquareCard,
} from '@schooly/style';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { ModalContent, ModalFooter } from '../../../components/uikit-components/Modal/Modal.styled';
import { ModalAccessDenied } from '../../../components/uikit-components/Modal/ModalAccessDenied';
import { ModalHeader } from '../../../components/uikit-components/Modal/ModalHeader';
import { useRouter } from '../../../context/router/useRouter';
import {
  TakeAttendanceModalContextProvider,
  useTakeAttendanceModal,
} from './TakeAttendanceModalContext';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import { DATE_FORMAT_DAY, DATE_FORMAT_MONTH_YEAR } from '@schooly/api';
import { WithJustificationChangeModal } from '@schooly/components/justifications';
import { TakeAttendanceModalContent } from './TakeAttendanceModalContent';

const TakeAttendanceModalWithProvider: FC = () => {
  const { goBack } = useRouter();
  const { getConfirmation } = useConfirmationDialog();
  const { canTakeAttendance, isModified, isSaving, attendanceFetching, onSave, allMarked } =
    useTakeAttendanceModal();

  const handleClose = useCallback(async () => {
    if (
      !isModified ||
      (await getConfirmation({
        textId: 'attendance-TakeAttendanceCloseConfirmation',
      }))
    ) {
      goBack();
    }
  }, [goBack, getConfirmation, isModified]);

  if (attendanceFetching) {
    return (
      <ModalLarge open onClose={handleClose}>
        <ModalContent active>
          <Loading />
        </ModalContent>
      </ModalLarge>
    );
  }

  if (!canTakeAttendance) {
    return (
      <ModalLarge open onClose={handleClose}>
        <TakeAttendanceModalHeader onClose={handleClose} />
        <ModalContent active>
          <ModalAccessDenied />
        </ModalContent>
      </ModalLarge>
    );
  }

  return (
    <ModalLarge open onClose={handleClose}>
      <TakeAttendanceModalHeader onClose={handleClose} />
      <ModalContent
        active
        sx={(theme) => ({
          pb: 0,
          [theme.breakpoints.down('lg')]: {
            p: theme.spacing(1, 4, 0),
          },
        })}
      >
        <TakeAttendanceModalContent />
      </ModalContent>
      <ModalFooter sx={{ m: 0 }} active>
        <Button
          startIcon={isSaving ? <Spin /> : <CheckIcon />}
          disabled={isSaving || !allMarked}
          onClick={onSave}
        >
          <FormattedMessage id="action-Save" />
        </Button>
      </ModalFooter>
    </ModalLarge>
  );
};

export const TakeAttendanceModal = () => {
  return (
    <WithJustificationChangeModal>
      <TakeAttendanceModalContextProvider>
        <TakeAttendanceModalWithProvider />
      </TakeAttendanceModalContextProvider>
    </WithJustificationChangeModal>
  );
};

const TakeAttendanceModalHeader: FC<{ onClose: VoidFunction }> = ({ onClose }) => {
  const { groupDetails, activePoint, isoDate } = useTakeAttendanceModal();

  const attendanceDate = newDateTimezoneOffset(isoDate);
  const attendanceDay = format(attendanceDate, DATE_FORMAT_DAY);
  const attendanceMonthYear = format(attendanceDate, DATE_FORMAT_MONTH_YEAR);

  return (
    <ModalHeader
      active
      title={
        <Stack flexDirection="row" alignItems="center" gap={3}>
          <SquareCard>
            <Typography mt={0.25} variant="h2">
              {attendanceDay}
            </Typography>
            <Typography variant="caption">{attendanceMonthYear}</Typography>
          </SquareCard>
          <Stack flex={1}>
            <Typography variant="body2" color="text.secondary">
              {activePoint?.label} <FormattedMessage id="attendance-Title" />
            </Typography>
            <Typography variant="h2">{groupDetails?.name}</Typography>
          </Stack>
        </Stack>
      }
      sx={{
        py: 1.5,
        alignItems: 'center',
        '& .ModalHeaderBackLink': {
          top: (theme) => theme.spacing(1.5),
        },
      }}
    >
      <ModalHeaderActions>
        <IconButton onClick={onClose}>
          <CrossIcon />
        </IconButton>
      </ModalHeaderActions>
    </ModalHeader>
  );
};
